import { BaseTable } from 'components/UI/BaseTable';
import { ExpandIcon } from 'components/UI/ExpandIcon';
import { WarningDialogTemplate } from 'components/UI/WarningDialogTemplate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openConfirmModal } from 'util/dialogs';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'util/config/constant';
import { OperatorExpandable } from '../OperatorExpandable';
import { deleteOperator, fetchOperationsList } from '../state/operation';
import * as selector from '../state/selector';
import { STATE_PROP } from './util/constant';
import { renderColumns } from './util/render-columns';
import { OperatorForm } from '../OperatorForm';
import { generateResponsibilities } from '../util/generateResponsibilities';
import { RESET_OPERATORS_STATE } from '../state/action';

export const OperatorList = () => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.OPERATOR);
  const dispatch = useDispatch();

  const { operatorsInfo } = useSelector((state) => ({
    operatorsInfo: selector.selectOperatorListInfo(state)
  }));

  const [editableFieldsOfRowIds, setEditableFieldsOfRowIds] = useState([]);
  const [emailPhoneFieldsRowData, setEmailPhoneFieldsRowData] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    stateProp: STATE_PROP
  });
  const [defaultFilterState, setDefaultFilterState] = useState({});
  const responsibilities = useMemo(() => generateResponsibilities(t), [t]);

  const operatorList = useMemo(() => {
    return operatorsInfo.data.filter((operator) => !operator.isDeleted);
  }, [operatorsInfo.data]);

  useEffect(
    () => () => {
      dispatch({
        type: RESET_OPERATORS_STATE
      });
    },
    [dispatch]
  );

  const handlePageChange = useCallback((page) => {
    setPagination((currentPagination) => ({
      ...currentPagination,
      page
    }));
  }, []);

  const handlePageLimit = useCallback((limit) => {
    setPagination((currentPagination) => ({
      ...currentPagination,
      page: 1,
      limit
    }));
  }, []);

  const onDeleteOperator = useCallback(
    (rowData) => {
      const message = (
        <span>{t('deletionMessage', { operatorName: rowData.name })}</span>
      );

      openConfirmModal(
        {
          title: t('confirm.announcementTitle'),
          content: <WarningDialogTemplate message={message} />,
          onOk: () => {
            dispatch(deleteOperator(rowData));
          }
        },
        t
      );
    },
    [dispatch, t]
  );

  const handleFilterChange = useCallback((newFilters) => {
    setDefaultFilterState(newFilters);
  }, []);

  const updateResponsibility = useCallback(
    (rowData) => {
      return responsibilities.map((responsibility) => {
        responsibility.value = rowData[responsibility.name];
        return responsibility;
      });
    },
    [responsibilities]
  );

  useEffect(() => {
    dispatch(
      fetchOperationsList({
        limit: pagination.limit,
        page: pagination.page,
        filters: defaultFilterState
      })
    );
  }, [
    defaultFilterState,
    dispatch,
    pagination.limit,
    pagination.page,
    operatorsInfo.effect
  ]);

  const columns = useMemo(() => {
    return renderColumns(
      t,
      onDeleteOperator,
      setEmailPhoneFieldsRowData,
      editableFieldsOfRowIds,
      emailPhoneFieldsRowData
    );
  }, [t, onDeleteOperator, editableFieldsOfRowIds, emailPhoneFieldsRowData]);

  const tableProps = useMemo(
    () => ({
      columns,
      pagination,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      showRowsPerPage: true,
      onPageChange: handlePageChange,
      onLimitChange: handlePageLimit,
      expandable: {
        expandedRowRender: (rowData) => {
          return (
            <OperatorExpandable
              emailPhoneFieldsRowData={emailPhoneFieldsRowData}
              rowData={rowData}
              responsibilities={updateResponsibility(rowData)}
            />
          );
        },
        expandIcon: ExpandIcon,
        expandIconColumnIndex: 5,
        onExpand: (expanded, record) => {
          if (expanded) {
            setEditableFieldsOfRowIds([...editableFieldsOfRowIds, record.id]);
          } else {
            const filteredEditableFields = editableFieldsOfRowIds.filter(
              function filterFields(value) {
                return value !== record.id;
              }
            );
            setEditableFieldsOfRowIds(filteredEditableFields);
          }
        },
        rowExpandable: (record) => editableFieldsOfRowIds.includes(record.id)
      },
      rowKey: 'id',
      dataSource: operatorList,
      loading: operatorsInfo.isLoading,
      className: 'table-yellow-header w-100'
    }),
    [
      columns,
      pagination,
      handlePageChange,
      handlePageLimit,
      operatorList,
      operatorsInfo.isLoading,
      emailPhoneFieldsRowData,
      updateResponsibility,
      editableFieldsOfRowIds
    ]
  );

  return (
    <div className="operator-table">
      <OperatorForm onFilterChange={handleFilterChange} />
      <BaseTable {...tableProps} />
    </div>
  );
};
